import React, { ReactElement, useState, useEffect, useRef, FocusEvent } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import TextArea from '@Common/TextArea';
import InputText from '@Common/InputText';
import FileChooser from '@Common/FileChooser';
import { actions as filesActions } from '@Actions/filesActions';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import { FILE_STATUSES } from '@Constants/constants';
import * as types from '@Constants/actionTypes';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import { Modal61Ref } from '@Types/common';
import { getUrlNoScheme, getUrlScheme } from '@Helpers/routing';
import { isValidUrl } from '@Helpers/validation';
import { LocationTaskLink, LocationTaskFile } from './Types';
import FileCell from './FileCell';
import ImageGalleryModal from './ImageGalleryModal';
import './LinksAndFiles.scss';

interface LinksAndFilesProps {
    locationTaskId: number;
    links: Array<LocationTaskLink> | undefined;
    files: Array<LocationTaskFile> | undefined;
    isMobile: boolean;
}

const LinksAndFiles = (props: LinksAndFilesProps): ReactElement => {
    const [links, setLinks] = useState<Array<LocationTaskLink>>(props.links ?? []); // Get the initial links...
    const [files, setFiles] = useState<Array<LocationTaskFile>>(props.files ?? []); // ... and files from props but we manage them hereafter
    const [isFileFormValid, setIsFileFormValid] = useState<boolean>(true);
    const [isSavingLink, setIsSavingLink] = useState<boolean>(false);
    const [isSavingFile, setIsSavingFile] = useState<boolean>(false);
    const [activeImageIndex, setActiveImageIndex] = useState<any>({ index: -1, opened: 0 });
    const [editLinkIds, setEditLinkIds] = useState<Array<number>>([]);  // The ids of existing links...
    const [editFileIds, setEditFileIds] = useState<Array<number>>([]);  // ... and files that are currently in edit mode

    const dispatch: Dispatch = useDispatch();
    const downloadFile = async (fileName: string) => dispatch<any>(filesActions.download(fileName));
    const createLink = async (link: LocationTaskLink) => dispatch<any>(locationTaskActions.createLink(link));
    const updateLink = async (link: LocationTaskLink) => dispatch<any>(locationTaskActions.updateLink(link));
    const uploadNewFile = async (request) => dispatch<any>(locationTaskActions.uploadFile(request));
    const replaceFile = async (request) => dispatch<any>(locationTaskActions.updateFile(request));
    const deleteFile = async (request) => dispatch<any>(locationTaskActions.deleteFile(request));

    const galleryModalRef = useRef<Modal61Ref>(null);

    const linksAndFilesCount = (links.length) + (files.length);

    const onAddFile = () => {
        const newFiles = files.slice();

        // Add a new file placeholder at the start of the list so it is visible immediately
        newFiles.unshift({
            id: 0,
            locationTaskId: props.locationTaskId,
            name: "",
            editingName: "",
            description: "",
            editingDescription: "",
            extension: "",
            fileStatusId: FILE_STATUSES.Pending,
            fileName: "",
            storageName: "",
            imageObjectURL: "",
            isActive: true,
            file: null,
        });

        setFiles(newFiles);
    }

    const onEditFile = (id: number) => {
        const newFiles = files.slice();
        const index = newFiles.findIndex(x => x.id === id);
        const file = newFiles[index];
        file.editingName = file.name;
        file.editingDescription = file.description ?? '';
        newFiles.splice(index, 1, file);
        const updatedList = [...editFileIds, file.id];
        setEditFileIds(updatedList);
    };

    const updateFile = (file, fieldName, fieldValue) => {
        const newFiles = files.slice();
        const index = newFiles.findIndex(x => x.id === file.id);
        file[fieldName] = fieldValue;
        newFiles.splice(index, 1, file);
        setFiles(newFiles);
    }

    const onCancelFile = (file: LocationTaskFile) => {
        const newFiles = files.slice();

        if (file.id === 0) {
            // Remove the first file from the list, which is the one that was being added
            newFiles.shift();
        } else if (isFileInEdit(file.id)) {
            // Clear the editing values
            file.editingName = "";
            file.editingDescription = "";
            file.showFileInput = false;
            const index = newFiles.findIndex(x => x.id === file.id);
            newFiles.splice(index, 1, file);

            // Clear the file from the edit list
            const updatedEditIds = editFileIds.filter(x => x !== file.id);
            setEditFileIds(updatedEditIds);
        }

        setIsFileFormValid(true);
        setFiles(newFiles);
    }

    const onDeleteFile = async (file: LocationTaskFile) => {
        try {
            setIsSavingFile(true);

            const request = {
                ...file,
                isActive: false, // Deactivate the file
                locationTaskId: props.locationTaskId,
            };
            const response = await deleteFile(request);

            if (response.type === types.DELETE_LOCATION_TASK_FILE_SUCCESS) {
                const newFiles = files.slice();
                const index = newFiles.findIndex(x => x.id === file.id);
                newFiles.splice(index, 1);
                setFiles(newFiles);
                Notification("success", "File Deleted", "");
            } else if (response.type === types.DELETE_LOCATION_TASK_FILE_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsSavingFile(false);
        }
    }

    const onFileReplaced = (file: LocationTaskFile, e: React.ChangeEvent<HTMLInputElement>) => {
        const replacedFile = e.target.files?.[0];
        if (replacedFile) {
            updateFile(file, "file", replacedFile);
        }
    }

    const validateFile = (file: LocationTaskFile) => {
        const isNewFile = file.id === 0;
        const hasFileBytes = file.file && file.file.name && file.file.name.trim().length > 0;
        const hasRequiredName = file.editingName && file.editingName.trim().length > 0;
        const isValidNewFile = isNewFile && hasFileBytes && hasRequiredName;

        const isExistingFile = !isNewFile;
        // Is replacing file and file has been chosen, or is not replacing file
        const hasRequiredFileReplacement = (file.showFileInput && hasFileBytes) || !file.showFileInput;
        const isValidExistingFile = isExistingFile && hasRequiredName && hasRequiredFileReplacement;

        return isValidNewFile || isValidExistingFile;
    };

    const onSaveFile = async (file: LocationTaskFile) => {
        let result = false;
        if (validateFile(file)) {
            setIsFileFormValid(true);
            setIsSavingFile(true);

            const request = {
                ...file,
                name: file.editingName,
                description: file.editingDescription,
                //fileStatusId: file.fileStatusId,
                locationTaskId: props.locationTaskId,
            };

            if (file.id === 0) {
                try {
                    const response = await uploadNewFile(request);
                    if (response.type === types.UPLOAD_LOCATION_TASK_FILE_SUCCESS) {
                        if (response.payload && response.payload.file) {
                            handleSavedFile(response.payload.file, true);
                            Notification('success', 'Task Updated', 'File has been saved successfully');
                            result = true;
                        }
                    } else if (response.type === types.UPLOAD_LOCATION_TASK_FILE_FAILURE) {
                        Notification('error', 'Error', response.payload.message);
                    }
                } catch (error) {
                    Notification('error', 'Error', error);
                } finally {
                    setIsSavingFile(false);
                }
            } else {
                try {
                    const response = await replaceFile(request);
                    if (response.type === types.UPDATE_LOCATION_TASK_FILE_SUCCESS) {
                        if (response.payload && response.payload.file) {
                            handleSavedFile(response.payload.file);
                            Notification('success', 'Task Updated', 'File has been saved successfully');
                            result = true;
                        }
                    } else if (response.type === types.UPDATE_LOCATION_TASK_FILE_FAILURE) {
                        Notification('error', 'Error', response.payload.message);
                    }
                } catch (error) {
                    Notification('error', 'Error', error);
                } finally {
                    setIsSavingFile(false);
                }
            }
        } else {
            setIsFileFormValid(false);
        }

        return result;
    };

    const handleSavedFile = (file: LocationTaskFile, isNew: boolean = false) => {
        if (isNew) {
            // For new files, add the saved file to the list
            setFiles(prev => [file, ...prev.filter(f => f.id !== 0)]); // Remove the temporary file with id 0
            return;
        }

        setFiles(prev => {
            const newFiles = prev.slice();
            const index = newFiles.findIndex(x => x.id === file.id);

            if (index >= 0) {
                // Replace existing file
                newFiles.splice(index, 1, file);
            } else {
                // Insert new file (shouldn't happen in normal flow, but safety net)
                newFiles.push(file);
            }

            return newFiles;
        });

        // Clear the file from the edit list
        setEditFileIds(prev => prev.filter(x => x !== file.id));
    };

    // this is for private files that were converted to links (link.baselineTaskLinkId != null)
    const onDownloadLink = async (link, e) => {
        e.preventDefault();
        try {
            // extract the file name from the link.url
            const start = link.url.indexOf("=") + 1;
            const fileName = link.url.substr(start, link.url.length - start);

            await downloadFile(fileName);
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    const showGallery = (fileName: string) => {
        const idx = props.files?.filter(f => f.imageObjectURL).findIndex(f => f.name === fileName) ?? 0;
        setActiveImageIndex({ index: idx, opened: activeImageIndex.opened + 1 });
    }

    useEffect(() => {
        // Show the gallery modal when React gets around to setting the active image index
        if (activeImageIndex.index >= 0) {
            galleryModalRef.current?.show();
        }
    }, [activeImageIndex]);

    const isLinkInEdit = (id: number) => {
        return id === 0 || editLinkIds.some(x => x === id);
    };

    const isFileInEdit = (id: number) => {
        return id === 0 || editFileIds.some(x => x === id);
    };

    const onAddLink = () => {

        const nextOrdinal = links.reduce((max, current) => Math.max(max, current.ordinal), 0) + 1;
        const newLinks = links.slice();
        newLinks.push({
            id: 0,
            locationTaskId: props.locationTaskId,
            baselineTaskLinkId: null,
            customerFileId: null,
            ordinal: nextOrdinal,
            name: "",
            editingName: "",
            url: "",
            editingUrl: "",
            isActive: true
        });

        setLinks(newLinks);
    };

    const onEditLink = (index: number) => {
        const newLinks = links.slice();
        const link = newLinks[index];
        link.editingUrl = link.url;
        link.editingName = link.name;
        newLinks.splice(index, 1, link);

        const updatedList = [...editLinkIds, link.id];
        setEditLinkIds(updatedList);
    };

    const onRemoveLink = async (index: number) => {
        try {
            const link = links[index];
            link.isActive = false;
            setIsSavingLink(true);
            const response = await updateLink(link);
            if (response.type === types.UPDATE_LOCATION_TASK_LINK_SUCCESS) {
                Notification("success", "Link Deleted Successfully", "");
                const newLinks = links.slice();
                newLinks.splice(index, 1);
                setLinks(newLinks);
            } else if (response.type === types.UPDATE_LOCATION_TASK_LINK_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsSavingLink(false);
        }
    };

    const onLinkInputUrlBlur = (e: FocusEvent<HTMLInputElement>, index: number) => {

        if (e.target.value === '')
            return;

        // For invalid input show error hint & change class on input
        const hintElem = document.querySelector(`[data-link-id="${index}"]`) as HTMLElement;
        if (hintElem) {
            if (!isValidUrl(`https://${e.target.value}`)) {
                e.target.className = "form-control is-invalid";
                hintElem.style.display = "inline";
            } else {
                e.target.className = "form-control";
                hintElem.style.display = "none";
            }
        }
    };

    const onLinkInputChange = (e, index) => {
        let { name, value } = e.target;

        const newList = links.slice();

        if (name === 'editingUrl' && value) {
            // Keep the url's scheme
            const url = newList[index].url;
            value = url ? `${getUrlScheme(url)}${value}` : `https://${value}`
        }

        newList[index][name] = value;
        setLinks(newList);
    };

    const onCancelLink = (link: LocationTaskLink) => {
        const newLinks = links.slice();

        if (link.id === 0) {
            // Remove the last link from the list, which is the one that was being added
            newLinks.pop();
        } else if (isLinkInEdit(link.id)) {
            // Clear the editing values
            link.editingUrl = "";
            link.editingName = "";
            const index = newLinks.findIndex(x => x.ordinal === link.ordinal);
            newLinks.splice(index, 1, link);

            // Clear the link from the edit list
            const updatedEditIds = editLinkIds.filter(x => x !== link.id);
            setEditLinkIds(updatedEditIds);
        }

        setLinks(newLinks);
    }

    const onSaveLink = async (link: LocationTaskLink, index: number) => {
        let result = false;

        // Validate the temporary in-progress field values to see if they're valid
        const isLinkValid =
            link.editingUrl &&
            link.editingUrl.trim().length > 0 &&
            link.editingName &&
            link.editingName.trim().length > 0 &&
            isValidUrl(link.editingUrl);

        if (isLinkValid) {
            // Set the temporary in-progress field values to the actual field values
            if (link.editingUrl) {
                link.url = link.editingUrl;
            }
            if (link.editingName) {
                link.name = link.editingName;
            }

            try {
                setIsSavingLink(true);

                if (link.id === 0) {
                    const response = await createLink(link);
                    if (response.type === types.CREATE_LOCATION_TASK_LINK_SUCCESS) {
                        updateSavedLinkInList(response.payload.link);
                        Notification('success', 'Link Added Successfully', '');
                        result = true;
                    } else if (response.type === types.CREATE_LOCATION_TASK_LINK_FAILURE) {
                        Notification('error', 'Error', response.payload.message);
                    }
                } else if (isLinkInEdit(link.id)) {
                    const response = await updateLink(link);
                    if (response.type === types.UPDATE_LOCATION_TASK_LINK_SUCCESS) {
                        updateSavedLinkInList(response.payload.link);
                        Notification('success', 'Link Saved Successfully', '');
                        result = true;
                    } else if (response.type === types.UPDATE_LOCATION_TASK_LINK_FAILURE) {
                        Notification('error', 'Error', response.payload.message);
                    }
                }
            } catch (error) {
                Notification('error', 'Error', error);
            }
            finally {
                setIsSavingLink(false);
            }
        } else {
            const hintElem = document.querySelector(`[data-link-id="${index}"]`) as HTMLElement;
            if (hintElem) {
                hintElem.style.display = "inline";
            }
        }

        return result;
    };

    const updateSavedLinkInList = (link: LocationTaskLink) => {
        const newLinks = links.slice();
        const index = newLinks.findIndex((x) => x.ordinal === link.ordinal);
        newLinks.splice(index, 1, link);
        setLinks(newLinks);

        // Clear the link from the edit list
        const updatedEditIds = editLinkIds.filter(x => x !== link.id);
        setEditLinkIds(updatedEditIds);
    };

    const showAddEditLinks = !(links.filter(x => isLinkInEdit(x.id)).length) && !(files.filter(x => isFileInEdit(x.id)).length);

    return (
        <div className='links-and-files mb-3'>

            <div className='row'>
                <div className='col-12'>
                    <div className="links-and-files-panel card">

                        <div className='card-header'>
                            <h3 className='card-title'>
                                <i className='fas fa-paperclip me-2'></i>
                                Links & Files
                                {links || files ? (
                                    <span className='badge bg-secondary ms-2'>
                                        {(links?.length || 0) + (files?.length || 0)}
                                    </span>
                                ) : null}
                            </h3>
                            <div style={{ display: 'flex', gap: '12px' }}>

                                <div className={`link-with-icon ${!showAddEditLinks || props.isMobile ? 'not-allowed' : ''}`}
                                    onClick={showAddEditLinks && !props.isMobile ? onAddLink : undefined}>
                                    <i className={'far fa-circle-plus'}></i>
                                    <button type='button' title="Add Link" className="btn btn-link" disabled={!showAddEditLinks || props.isMobile}>
                                        Add Link
                                    </button>
                                </div>
                                <div className={`link-with-icon ${!showAddEditLinks || props.isMobile ? 'not-allowed' : ''}`}
                                    onClick={showAddEditLinks && !props.isMobile ? onAddFile : undefined}>
                                    <i className={'far fa-circle-plus'}></i>
                                    <button type='button' title="Add File" className="btn btn-link" disabled={!showAddEditLinks || props.isMobile}>
                                        Add File
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={`panel-body ${linksAndFilesCount === 0 ? 'empty' : ''}`}>
                            <div className='links-list'>
                                {links.map((link, index) => {
                                    if (link.baselineTaskLinkId && link.customerFileId) {
                                        return (
                                            <div key={link.ordinal} className='link-list-item-no-actions'>
                                                <button type='button' className="btn btn-link" onClick={(e) => onDownloadLink(link, e)}>
                                                    {link.name}
                                                </button>
                                                <div className='list-item-icon' title='Cannot remove or edit baseline task links'>
                                                    <i className='fa-regular fa-pen-to-square'></i>
                                                    <i className='fa-regular fa-trash-can'></i>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        if (!isLinkInEdit(link.id)) {
                                            return (
                                                <div key={link.ordinal} className={`link-list-item${link.baselineTaskLinkId || !showAddEditLinks ? '-no-actions' : ''}`}>
                                                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                                                    <div className='list-item-icon' title={`${link.baselineTaskLinkId ? 'Cannot remove or edit baseline task links' : ''}`}>
                                                        <i className='fa-regular fa-pen-to-square' onClick={() => onEditLink(index)}></i>
                                                        <i className='fa-regular fa-trash-can' onClick={() => onRemoveLink(index)}></i>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div key={link.ordinal}>
                                                    <LoadingIndicator isLoading={isSavingLink}>
                                                        <div className='link-list-item-editing'>
                                                            <div className='link-url'>
                                                                <div className='input-group'>
                                                                    <span className='input-group-text'>{getUrlScheme(link.editingUrl)}</span>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        name='editingUrl'
                                                                        id={`linkUrl_${index}`}
                                                                        value={getUrlNoScheme(link.editingUrl)}
                                                                        onChange={e => onLinkInputChange(e, index)}
                                                                        onBlur={e => onLinkInputUrlBlur(e, index)}
                                                                    />
                                                                </div>
                                                                <span className="url-error-hint" data-link-id={index}>Please enter a valid link.</span>
                                                            </div>

                                                            <input
                                                                name='editingName'
                                                                id={`linkName_${index}`}
                                                                type='text'
                                                                placeholder='Name your link'
                                                                className='form-control'
                                                                value={link.editingName}
                                                                onChange={(e) => onLinkInputChange(e, index)}
                                                            />

                                                            <div className='edit-buttons'>
                                                                <button type='button' className='btn btn-link btn-sm' onClick={() => onCancelLink(link)}>Cancel</button>
                                                                <button type='button' className='btn btn-primary btn-sm' onClick={() => onSaveLink(link, index)}>Save</button>
                                                            </div>
                                                        </div>
                                                    </LoadingIndicator>
                                                </div>
                                            );
                                        }
                                    }
                                })}
                            </div>

                            <div className='files-list'>
                                {files.map((file) => {

                                    if (!isFileInEdit(file.id)) {
                                        return <FileCell
                                            key={file.fileName}
                                            file={file}
                                            setIsSavingFile={setIsSavingFile}
                                            showGallery={showGallery}
                                            editFile={onEditFile}
                                            deleteFile={onDeleteFile}
                                            handleSavedFile={handleSavedFile}
                                            isMobile={props.isMobile}
                                        />;
                                    } else {
                                        return (
                                            <div key={file.id} className="file-list-item-editing-wrapper">
                                                <LoadingIndicator isLoading={isSavingFile}>
                                                    <form noValidate className={`file-list-item-editing ${isFileFormValid ? '' : 'was-validated'}`}>

                                                        <InputText name="fileName" label="File Name" type="text" placeholder="File Name"
                                                            isFocused={true}
                                                            value={file.editingName}
                                                            onChange={(e) => updateFile(file, "editingName", e.target.value)}
                                                            isRequired={true}
                                                            errorMessage="File Name is required"
                                                            maxLength={100} />

                                                        <TextArea rows={5} maxLength={500} name="fileDescription" label="File Description" type="text" placeholder="File Description"
                                                            value={file.editingDescription}
                                                            onChange={(e) => updateFile(file, "editingDescription", e.target.value)} />
                                                        <div className="remaining-chars text-end mb-3">{file.editingDescription.length}/500</div>

                                                        {
                                                            file.id === 0 && !props.isMobile &&
                                                            <div className="file-chooser-wrapper">
                                                                <FileChooser name="file"
                                                                    onChange={(e) => onFileReplaced(file, e)}
                                                                    isRequired={true}
                                                                    errorMessage="A file is required"
                                                                />
                                                            </div>
                                                        }
                                                        {
                                                            file.id !== 0 && !file.showFileInput && !props.isMobile &&
                                                            <div className="edit-buttons mb-3">
                                                                <button type="button" className="btn btn-link btn-sm" onClick={() => updateFile(file, "showFileInput", true)}>Replace File</button>
                                                            </div>
                                                        }
                                                        {
                                                            file.id !== 0 && file.showFileInput && !props.isMobile &&
                                                            <div className="file-chooser-wrapper">
                                                                <FileChooser name="file"
                                                                    onChange={(e) => onFileReplaced(file, e)}
                                                                    isRequired={true}
                                                                    errorMessage="A file is required"
                                                                />
                                                            </div>
                                                        }

                                                        <div className='edit-buttons'>
                                                            <button type='button' className='btn btn-link btn-sm' onClick={() => onCancelFile(file)}>Cancel</button>
                                                            <button type='button' className='btn btn-primary btn-sm' onClick={() => onSaveFile(file)}>Save</button>
                                                        </div>

                                                    </form>
                                                </LoadingIndicator>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ImageGalleryModal modalRef={galleryModalRef} files={props.files?.filter(f => f.imageObjectURL)} initialIndex={activeImageIndex.index} />
        </div>
    );
};

export default LinksAndFiles;
